import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

import {
  frameAssetManifest,
  ornamentAssetManifest,
  type FrameVariant,
} from '../assets/frameAssetManifest.js';

type FrameElement = 'article' | 'div' | 'header' | 'section';

export interface OrnateFrameProps extends HTMLAttributes<HTMLElement> {
  as?: FrameElement;
  variant: FrameVariant;
  cornerSize?: number;
  edgeThickness?: number;
  overhang?: number;
  children: ReactNode;
}

interface FrameVariables extends CSSProperties {
  '--px-frame-corner-size-base': string;
  '--px-frame-edge-thickness-base': string;
  '--px-frame-overhang': string;
}

/**
 * A transparent corner-and-edge overlay around an ordinary content element.
 * Corners retain their aspect ratio; edge strips stretch along one axis only.
 */
export function OrnateFrame({
  as: Component = 'div',
  variant,
  cornerSize,
  edgeThickness,
  overhang,
  className = '',
  children,
  style,
  ...props
}: OrnateFrameProps) {
  const assets = frameAssetManifest[variant];
  const variables: FrameVariables = {
    '--px-frame-corner-size-base': `${cornerSize ?? assets.cornerSize}px`,
    '--px-frame-edge-thickness-base': `${edgeThickness ?? assets.edgeThickness}px`,
    '--px-frame-overhang': `${overhang ?? assets.overhang}px`,
    ...style,
  };

  return (
    <Component
      {...props}
      className={`px-ornate-frame px-ornate-frame--${variant} ${className}`.trim()}
      style={variables}
    >
      {children}
      <span className="px-ornate-frame__art" aria-hidden="true">
        <img
          className="px-ornate-frame__edge px-ornate-frame__edge--top"
          src={assets.edges.horizontal}
          alt=""
        />
        <img
          className="px-ornate-frame__edge px-ornate-frame__edge--right"
          src={assets.edges.vertical}
          alt=""
        />
        <img
          className="px-ornate-frame__edge px-ornate-frame__edge--bottom"
          src={assets.edges.horizontal}
          alt=""
        />
        <img
          className="px-ornate-frame__edge px-ornate-frame__edge--left"
          src={assets.edges.vertical}
          alt=""
        />
        <img
          className="px-ornate-frame__corner px-ornate-frame__corner--tl"
          src={assets.corners.topLeft}
          alt=""
        />
        <img
          className="px-ornate-frame__corner px-ornate-frame__corner--tr"
          src={assets.corners.topRight}
          alt=""
        />
        <img
          className="px-ornate-frame__corner px-ornate-frame__corner--bl"
          src={assets.corners.bottomLeft}
          alt=""
        />
        <img
          className="px-ornate-frame__corner px-ornate-frame__corner--br"
          src={assets.corners.bottomRight}
          alt=""
        />
      </span>
    </Component>
  );
}

export type FrameConvenienceProps = Omit<OrnateFrameProps, 'variant'>;

export function PageFrame(props: FrameConvenienceProps) {
  return <OrnateFrame {...props} variant="page" />;
}

/**
 * Canonical corner size for every `hero` frame instance (`GamesPageHeader`,
 * `ProfileHero`, `FrameShowcasePage`'s demo). All three independently
 * converged on 34px mobile / 42px tablet+ — which also matches
 * `frameAssetManifest`'s own documented "intended rendered corner size" of
 * 42 at tablet+ — but nothing enforced that agreement; a fourth caller could
 * as easily have drifted. Baked into `HeroFrame` itself, the way
 * `PagePlate` bakes in the page shell's geometry, rather than left as a
 * string each caller must remember to paste.
 *
 * Kept as a literal class string, and not composed from a token at runtime:
 * Tailwind discovers utilities by scanning source text, so a class
 * assembled from variables is never emitted.
 *
 * `ProfileAppearancePreview`'s hero is deliberately NOT this: it is a small,
 * non-responsive Settings preview that passes `cornerSize={28}` as a prop
 * instead (the one live use of that prop) rather than any breakpoint pair,
 * so it stays on plain `OrnateFrame`, not `HeroFrame`.
 */
const HERO_FRAME_CLASS = '[--px-frame-corner-size:34px] tablet:[--px-frame-corner-size:42px]';

export function HeroFrame({ className = '', ...props }: FrameConvenienceProps) {
  return (
    <OrnateFrame {...props} variant="hero" className={`${HERO_FRAME_CLASS} ${className}`.trim()} />
  );
}

/**
 * Canonical corner size for every `game-card` frame instance
 * (`GameCatalogCard`, `GameProfileStatsCard`, `FrameShowcasePage`'s demo).
 * Closes a real drift, not just a duplicated string: `GameProfileStatsCard`
 * and the dev showcase both used 24/28 (which matches
 * `frameAssetManifest`'s own documented "intended rendered corner size" of
 * 28 at tablet+), while `GameCatalogCard` independently drifted to 26/30 —
 * a game card and a profile stats card rendered the same frame asset at two
 * different sizes for no reason traceable to either layout.
 *
 * This is also why the size lives here, baked into `GameCardFrame`, rather
 * than staying a `cornerSize`/`edgeThickness` prop callers pass in: every
 * real caller was setting the corner size via a Tailwind arbitrary-property
 * override in `className` instead of the props — and a `className` utility
 * silently wins over the props' underlying CSS variable regardless of what
 * either says, because Tailwind's utilities layer is emitted after the
 * component-layer rule that reads the props' value. Two call sites setting
 * that override independently is exactly how they drifted apart with no
 * type or lint rule able to see it. Structural agreement, not a rule to
 * remember: a `GameCardFrame` caller no longer sets this at all.
 */
const GAME_CARD_FRAME_CLASS = '[--px-frame-corner-size:24px] tablet:[--px-frame-corner-size:28px]';

export function GameCardFrame({ className = '', ...props }: FrameConvenienceProps) {
  return (
    <OrnateFrame
      {...props}
      variant="game-card"
      className={`${GAME_CARD_FRAME_CLASS} ${className}`.trim()}
    />
  );
}

export interface OrnamentalRuleProps extends HTMLAttributes<HTMLSpanElement> {
  accent?: 'small' | 'large';
}

export function OrnamentalRule({
  accent = 'small',
  className = '',
  ...props
}: OrnamentalRuleProps) {
  return (
    <span {...props} className={`px-ornamental-rule ${className}`.trim()} aria-hidden="true">
      <img src={ornamentAssetManifest.ruleEndLeft} alt="" />
      <span className="px-ornamental-rule__line" />
      <img
        className={
          accent === 'large'
            ? 'px-ornamental-rule__diamond px-ornamental-rule__diamond--large'
            : 'px-ornamental-rule__diamond'
        }
        src={
          accent === 'large'
            ? ornamentAssetManifest.diamondLarge
            : ornamentAssetManifest.diamondSmall
        }
        width={accent === 'large' ? 18 : 12}
        height={accent === 'large' ? 18 : 12}
        alt=""
      />
      <span className="px-ornamental-rule__line" />
      <img src={ornamentAssetManifest.ruleEndRight} alt="" />
    </span>
  );
}
